import { useMantineTheme } from "@mantine/core"
import {
  ColumnPinningState,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table"
import { CHECKBOX_COLUMN_ID } from "@tfg/ui"
import { useCallback, useMemo } from "react"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"

type Props<TData> = {
  initialColumnPinning?: ColumnPinningState
} & Omit<TableOptions<TData>, "getCoreRowModel" | "getSortedRowModel">

export const useCustomReactTable = <TData>({
  initialColumnPinning,
  ...config
}: Props<TData>) => {
  const {
    orderedColumns,
    columnPinning,
    columnVisibility,
    setOrderedColumns,
    setColumnPinning,
    setColumnVisibility,
  } = useHeaderControlsContext()
  const theme = useMantineTheme()

  const initiateColumnsOrder = useCallback(
    (columns: ColumnDef<TData>[]) =>
      columns
        .map((col: ColumnDef<TData>) => {
          const hasIdKey = typeof col === "object" && "id" in col
          const hasAccessorKey = typeof col === "object" && "accessorKey" in col

          if (hasIdKey) {
            return col.id
          }
          if (hasAccessorKey) {
            return String(col.accessorKey).split(".").join("_")
          }
          return
        })
        .filter(Boolean),
    []
  )
  const { state, ...rest } = config

  const defaultTableConfig = useMemo(
    () => ({
      defaultColumn: {
        size: theme.other.table.headers.size,
        minSize: theme.other.table.headers.minSize,
        maxSize: theme.other.table.headers.maxSize,
      },
      state: {
        columnPinning: columnPinning ?? {
          left: [CHECKBOX_COLUMN_ID, ...(initialColumnPinning?.left || [])],
        },
        columnVisibility,
        columnOrder:
          orderedColumns.length > 0
            ? orderedColumns
            : initiateColumnsOrder(config.columns),
        ...state,
      },
      onColumnPinningChange: setColumnPinning,
      onColumnVisibilityChange: setColumnVisibility,
      onColumnOrderChange: setOrderedColumns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [
      columnPinning,
      columnVisibility,
      orderedColumns,
      state,
      theme,
      config.columns,
      initialColumnPinning?.left,
      initiateColumnsOrder,
      setColumnPinning,
      setColumnVisibility,
      setOrderedColumns,
    ]
  )

  const table = useMemo(
    () => ({
      ...defaultTableConfig,
      ...rest,
    }),
    [defaultTableConfig, rest]
  )

  return useReactTable(table)
}
